// deps
import React from "react";

// internal-deps
import type { ApiResponse } from "@swiggy-private/payment-types";
import { IEMIBankPlan } from "@swiggy-private/dls-web-recipe-common-web/dist/components/EMICard";

/**
 * Response structure for EMI details API containing available EMI plans and configuration
 */
export interface IEMIResponse {
    /** Page heading text to display in the EMI selection UI */
    pageHeading: string;
    /** Array of section identifiers to control UI display */
    displaySections: string[];
    /** Total cart amount for EMI calculation */
    cartTotalAmount: number;
    /** Available EMI plans for new cards */
    emiPlans: IEMIBankPlan[];
    /** Available EMI plans for saved cards */
    savedCardEmiPlans: IEMIBankPlan[];
}

/**
 * Details of the currently selected EMI option by the user
 */
export interface IEMISelectionDetails {
    tenure: number;
    bankCode: string;
    emiType: string;
    emiBank: string;
    txnEmiType: string;
    cardReference: string | null;
    bankName: string;
}

/**
 * Context value interface for EMI selection state management
 * Provides methods to fetch EMI details, update selections, and manage loading states
 */
export interface EmiSelectionContextValue {
    /** Loading state for EMI data fetching */
    loadingEmi: boolean;
    /** Function to update EMI loading state */
    setLoadingEmi: (loadingEmi: boolean) => void;
    /** EMI response data containing available plans and configuration */
    emiResponse: IEMIResponse | null;
    /** Currently selected EMI details by the user */
    selectedEmiDetails: IEMISelectionDetails | null;
    /** Error state for EMI operations */
    emiError: Error | ApiResponse | null;
    /** Function to fetch EMI details */
    fetchEmiDetails: (cardReference?: string) => void;
    /** Function to update the selected EMI details */
    updateEmiSelection: (selection: IEMISelectionDetails) => void;
    /** Function to set EMI APIresponse data */
    setEmiResponse: (emiResponse: IEMIResponse) => void;
}

/**
 * React Context for managing EMI selection state across the application
 * Provides centralized state management for EMI plans, user selections, and loading states
 */
export const EmiSelectionContext = React.createContext<EmiSelectionContextValue>({
    loadingEmi: false,
    setLoadingEmi: () => void 0,
    fetchEmiDetails: () => void 0,
    updateEmiSelection: () => void 0,
    setEmiResponse: () => void 0,
    selectedEmiDetails: null,
    emiResponse: null,
    emiError: null,
});

if (__DEV__) {
    EmiSelectionContext.displayName = "EmiSelectionContext";
}
